import { combineReducers } from 'redux';
import Immutable from 'immutable';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type {
    AssetContainerModel,
    GenerateTranscriptResponse,
    MediaFolder,
    SlideBackgroundModelTypeEnum,
    TranscriptAssetType,
} from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    BackgroundVideoDurationSeconds,
    MaxVideoDurationSeconds,
    SourceEnum,
    TaskResultMessageTypeEnum,
} from 'js/src/client';
import { getMillisFromSeconds } from 'js/src/components/common/asset-trimmer/common';
import { media_reducers_map } from 'js/src/data/media/media-reducers';
import type {
    ImmutableSlideBackgroundModel,
    MediaFolderWithContents,
    ModifiedAssetGenerationData,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
} from 'js/src/data/media/media-types';
import {
    EDITOR_DAM_TAB_CONTEXTS,
    EDITOR_MUSIC_TAB_CONTEXTS,
    EDITOR_VOICEOVER_TAB_CONTEXTS,
    getImageAssetFromBackgroundAsset,
    getImmutableVideoAssetFromImmutableMediaResult,
    getVideoAssetFromBackgroundAsset,
    isImmutableAudioAsset,
    SEARCH_CONTEXT,
} from 'js/src/data/media/media-types';
import type StoreORM from 'js/src/data/store-orm';
import type { FileWithMetadata } from 'js/src/data/uploads';
import {
    assetContainerModelIsBackground,
    immutableAssetContainerModelIsAudio,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsFont,
    immutableAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import {
    GET_VALID_AUDIO_MIME_TYPES,
    GET_VALID_ICON_MIME_TYPES,
    GET_VALID_IMAGE_MIME_TYPES,
    GET_VALID_VIDEO_MIME_TYPES,
    type ImmutableMap,
} from 'js/src/libs/consts';
import { downloadUrlAsFile } from 'js/src/libs/download-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import UploadMediaController from 'js/src/libs/upload-media-controller';
import WebSocketClient from 'js/src/libs/websocket-client';

/**
 * Given a search context (ie. 'recommended-visuals') returns the corresponding asset type ('background')
 */
export function getAssetTypeForSearchContext(
    searchContext: SearchContextValueType,
): AssetContainerTypes {
    let category_type = AssetContainerTypes.BACKGROUND;

    if (
        (EDITOR_MUSIC_TAB_CONTEXTS as readonly string[]).includes(searchContext) ||
        (EDITOR_VOICEOVER_TAB_CONTEXTS as readonly string[]).includes(searchContext)
    ) {
        category_type = AssetContainerTypes.AUDIO;
    }
    // Check if it's an icon-specific context (recommended icons or uploaded elements)
    if (
        searchContext === SEARCH_CONTEXT.RECOMMENDED_ICONS ||
        searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS
    ) {
        category_type = AssetContainerTypes.ICON;
    }
    return category_type;
}

export function isAnimatedSticker(thumbnailUrl: string | null | undefined): boolean {
    if (!thumbnailUrl) return false;
    const url = URL.parse(thumbnailUrl, window.location.origin);
    return url?.pathname.toLowerCase().endsWith('.gif') ?? false;
}

export function getIsFilterVisible(searchContext: SearchContextValueType): boolean {
    const categories_with_filters_enabled: SearchContextValueType[] = [
        SEARCH_CONTEXT.RECOMMENDED_VISUALS,
        SEARCH_CONTEXT.UPLOADED_VISUALS,
        SEARCH_CONTEXT.RECENT_VISUALS,
        SEARCH_CONTEXT.FAVORITE_VISUALS,
        SEARCH_CONTEXT.STOCK_AUDIO,
        SEARCH_CONTEXT.FREEPIK_STICKERS,
        ...EDITOR_DAM_TAB_CONTEXTS,
    ];
    return categories_with_filters_enabled.includes(searchContext);
}

export function getIsSuggestionsVisible(searchContext: SearchContextValueType): boolean {
    const disabled_categories: SearchContextValueType[] = [
        SEARCH_CONTEXT.UPLOADED_VOICEOVERS,
        SEARCH_CONTEXT.WAVEFORMS,
        ...EDITOR_DAM_TAB_CONTEXTS,
    ];
    return !disabled_categories.includes(searchContext);
}

export function getShouldResetSearchResultsOnLoad(searchContext: SearchContextValueType): boolean {
    const categories_that_should_reset_search_on_load: SearchContextValueType[] = [
        SEARCH_CONTEXT.FAVORITE_VISUALS,
        SEARCH_CONTEXT.FAVORITE_AUDIO,
    ];
    return categories_that_should_reset_search_on_load.includes(searchContext);
}

/**
 * Returns a modified folder that has properties necessary for uploads storage
 * Used when setting all upload folders for the first time or creating a new folder
 * Converts MediaFolder -> MediaFolderWithContents
 */
export function setUploadsFolderStore(
    folder: ImmutableMap<MediaFolder>,
): ImmutableMap<MediaFolderWithContents> {
    // import all pre-defined media reducers, initialize all reducers with their initial states
    const folder_reducer_map = combineReducers(media_reducers_map);
    // get the initial states of media-search related reducers
    const initialized = folder_reducer_map(undefined, { type: 'INIT_FOLDERS' });
    // manually set all_uploads_folder[folderId] with initial state
    return folder.merge(
        Immutable.fromJS(initialized),
    ) as unknown as ImmutableMap<MediaFolderWithContents>;
}

/**
 * Resets the state of the folder reducer to initial state. Usually used when navigating
 * out of a media folder.
 *
 * @param {orm} folderOrm
 * @param {int} folderId
 * @returns {void} Reset state for folders at folderId
 */
export function resetUploadsFolderStore(
    folderOrm: StoreORM<MediaFolderWithContents>,
    folderId: number,
) {
    if (!folderOrm) {
        return;
    }

    const folder_reducer_map = combineReducers(media_reducers_map);
    const initialized = folder_reducer_map(undefined, {
        type: 'INIT_FOLDERS',
    });

    // reset all media-search related reducer values
    for (const [reducerKey, initialValue] of Object.entries(initialized)) {
        folderOrm.updateById(folderId, {
            key: [reducerKey],
            value: initialValue,
        });
    }
}

export function isAssetTypeMatchingSelectedAssetContainerType(
    asset: ImmutableMap<AssetContainerModel>,
    selectedContainerType: string | null,
    isSticker: boolean,
) {
    if (selectedContainerType === null) {
        return false;
    }
    if (isSticker) {
        return (
            (selectedContainerType === VideoData.MEDIA_ASSET_TYPE ||
                selectedContainerType === VideoData.ICON_ASSET_TYPE) &&
            (immutableAssetContainerModelIsBackground(asset) ||
                immutableAssetContainerModelIsIcon(asset))
        );
    }
    return (
        (selectedContainerType === VideoData.MEDIA_ASSET_TYPE &&
            immutableAssetContainerModelIsBackground(asset)) ||
        (selectedContainerType === VideoData.ICON_ASSET_TYPE &&
            immutableAssetContainerModelIsIcon(asset))
    );
}

export async function regenerateTranscriptForAsset(
    assetId: string,
    assetType: TranscriptAssetType,
    language_code: string | undefined,
) {
    const response = await ApiService.regenerateTranscriptForAssetCreate({
        body: {
            asset_uuid: assetId,
            asset_type: assetType,
            language_code,
        },
    });
    try {
        const ws = WebSocketClient.getInstance();
        const result = await ws.waitForMessage<GenerateTranscriptResponse>(
            response.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'generate_transcript',
        );
        return result.has_voiceover;
    } catch (pollError) {
        throw new Error('Failed to determine voiceover status');
    }
}

export function getAssetUuidFromAssetContainer(
    assetContainer: ImmutableMap<AssetContainerModel>,
): string | null {
    if (immutableAssetContainerModelIsBackground(assetContainer)) {
        return getAssetUuidFromBackgroundAsset(assetContainer.get(AssetContainerTypes.BACKGROUND));
    }
    if (immutableAssetContainerModelIsIcon(assetContainer)) {
        return assetContainer.get(AssetContainerTypes.ICON).get('uuid');
    }
    if (immutableAssetContainerModelIsAudio(assetContainer)) {
        return assetContainer.get(AssetContainerTypes.AUDIO).get('uuid');
    }
    if (immutableAssetContainerModelIsFont(assetContainer)) {
        return assetContainer.get(AssetContainerTypes.FONT).get('uuid');
    }
    if (isImmutableAudioAsset(assetContainer)) {
        return assetContainer.get('uuid');
    }
    return null;
}

export function getAssetUuidFromBackgroundAsset(
    backgroundAsset: ImmutableSlideBackgroundModel,
): string | null {
    return (
        getVideoAssetFromBackgroundAsset(backgroundAsset)?.get('uuid') ||
        getImageAssetFromBackgroundAsset(backgroundAsset)?.get('uuid') ||
        null
    );
}

export function isDAMSearchContext(searchContext: SearchContextValueType): boolean {
    return searchContext.includes('dam-');
}

export const assetIsTalkingHeadAndRequiresTrimming = (
    assetData: ImmutableMap<AssetContainerModel>,
) => {
    // if asset is talking head and longer than 10 mins, prompt user to trim first, return early
    const immutableVideoAssetData = getImmutableVideoAssetFromImmutableMediaResult(assetData);
    if (immutableVideoAssetData) {
        const isTalkingHeadMedia = immutableVideoAssetData.get('has_voiceover');
        if (isTalkingHeadMedia) {
            const assetDurationMillis = immutableVideoAssetData.get('duration');
            const maxTrimDurationMillis = getMillisFromSeconds(BackgroundVideoDurationSeconds.MAX);
            if (assetDurationMillis > maxTrimDurationMillis && immutableVideoAssetData) {
                return true;
            }
        }
    }

    return false;
};

export const talkingHeadPlaybackRegionRequiresTrimming = (mediaAsset: TimeAwareMediaAsset) => {
    // use endTime - startTime instead of duration to check if the playback region >10mins
    // if it is, the user wouldn't be able to publish the video, so we should prompt them to trim
    const playbackDurationMillis = (mediaAsset.endTime as number) - mediaAsset.startTime;
    const maxTrimDurationMillis = getMillisFromSeconds(MaxVideoDurationSeconds.LIMIT);
    if (playbackDurationMillis > maxTrimDurationMillis) {
        return true;
    }
    return false;
};

export function checkAssetUUID(uuid: string | null | undefined) {
    if (uuid === null || uuid === undefined) {
        throw new Error('Asset UUID is null');
    }
    return uuid;
}

export function findGenerationInList(
    generations: ModifiedAssetGenerationResponse[],
    mediaUuid: string,
    mediaType: SlideBackgroundModelTypeEnum,
) {
    if (!mediaUuid) {
        return null;
    }
    const index = generations.findIndex((generation) => {
        if (generation?.result?.asset) {
            const asset = generation.result.asset;
            if (!assetContainerModelIsBackground(asset)) {
                throw new Error('Expected asset to be a background');
            }
            return asset.background?.[mediaType]?.uuid === mediaUuid;
        }
        return false;
    });

    if (index !== -1) {
        const gen = generations[index]?.result;
        if (gen) {
            return { ...gen, index } as typeof gen & { index: number };
        }
    }
    return null;
}

export async function saveAIMedia(generation: ModifiedAssetGenerationData) {
    const { uuid: generationUuid, prompt } = generation;
    const mediaAsset = generation?.asset?.background?.image;
    const url = mediaAsset?.preview_url;
    const uuid = mediaAsset?.uuid;
    if (!url || !uuid) {
        throw new Error('No valid AI media found');
    }

    const file = await downloadUrlAsFile({ url, fileName: '' });
    return new Promise<ImmutableMap<AssetContainerModel>>((resolve, reject) => {
        // Upload the file to the bucket
        const mediaUploader = new UploadMediaController({
            file_list: [{ file }],
            asset_type: AssetContainerTypes.BACKGROUND,
            metadata: {
                uuid,
                source: SourceEnum.SOURCE_AI_GENERATED,
                source_id: url,
                license: VideoData.USER_PRIVATE_LICENSE,
                generation_uuid: generationUuid,
                description: prompt,
            },
            upload_succeeded_callback: (
                file: FileWithMetadata,
                newAsset: ImmutableMap<AssetContainerModel>,
            ) => {
                if (!newAsset || !immutableAssetContainerModelIsBackground(newAsset)) {
                    throw new Error('AI image uploaded is not a background asset');
                }
                resolve(newAsset);
            },
            upload_failed_callback: (file: FileWithMetadata, message: string, e: Error) => {
                ErrorReporter.capture(e, { extra: { message, generationUuid } });
                reject(e);
            },
        });
        mediaUploader.upload();
    });
}

export function getAssetIsInDatabase(source: SourceEnum) {
    return ![
        SourceEnum.SOURCE_DAM,
        SourceEnum.SOURCE_TENOR,
        SourceEnum.SOURCE_AI_AVATAR,
        SourceEnum.SOURCE_FREEPIK,
    ].includes(source);
}

export function isVideoMimeType(mimeType: string): boolean {
    return GET_VALID_VIDEO_MIME_TYPES().includes(mimeType);
}

export function isImageMimeType(mimeType: string, only_images: boolean): boolean {
    return GET_VALID_IMAGE_MIME_TYPES(only_images).includes(mimeType);
}

export function isIconMimeType(mimeType: string): boolean {
    return GET_VALID_ICON_MIME_TYPES().includes(mimeType);
}

export function isAudioMimeType(mimeType: string): boolean {
    return GET_VALID_AUDIO_MIME_TYPES().includes(mimeType);
}
